import { useRef, useState, type ChangeEvent } from "react";
import { ChevronDown, Upload, X } from "lucide-react";
import { AntennaMark, Button, Card, EmptyState, Icon, Picker, Skeleton, Textarea, useMinWidth } from "@cntrl/ui";
import { ApiError, errorMessage } from "../../app/hooks/apiError.js";
import { pushToast } from "../../app/toasts.js";
import { useUploadMedia, useMediaObjectUrl } from "../../app/hooks/useMedia.js";
import {
  useBranding,
  useUpdateBranding,
  useBandPhotos,
  useUpdatePhotos,
  useBios,
  useUpdateBios,
  type BrandingRead,
  type BandPhotosRead,
  type BandPhotoView,
  type BioRead,
} from "../../app/hooks/useBrandAssets.js";
import {
  EPK_ROLES, NO_PLACEMENT, PHOTO_SLOTS, effectivePlacement, type EpkRole,
} from "../../app/photoPlacement.js";
import { PHOTO_DRAG_TYPE, swapPhotoMedia } from "./photoSwap.js";
import { useUnsavedChanges, saveButtonState } from "./useUnsavedChanges.js";
import { MobilePhotoTiles } from "./MobilePhotoTiles.js";
import styles from "./CoreCards.module.scss";

// The EPK-usage placements. epk_role is free text server-side; the client offers these labels.
// Both bio ceilings mirror `BrandBioService`, which is the authority — the counter is the affordance,
// the 422 is the rule. FULL_BIO_MAX is ART-55 (2,000 chars ≈ 300 words).
const SHORT_BIO_MAX = 200;
const FULL_BIO_MAX = 2000;

/*
 * The Assets → "Core" CARDS — logos, the 5 photo slots (with the one-per-band Antenna avatar/cover
 * toggles + EPK roles), bios, and the version-snapshot restore panel (shot-29). The whole surface is
 * write-gated (assets:write): merch_manager and production don't reach it. Uploads ride the media flow
 * (useMedia); each branding/photos save snapshots the prior state server-side so a mistaken replace is
 * restorable.
 *
 * A CARD MODULE, not a screen: `AssetsScreen` composes these into the one stacked /assets page, which
 * both the artist shell and the roster lens mount (PO, 2026-09-01 — the roster gets the SAME Assets page
 * as the band, which is what legacy did: its roster shell embedded the artist screens themselves). The
 * standalone `BrandScreen` wrapper the roster tab used to mount is gone with that change; the assets
 * audit's note that "roster's assets tab uses BrandScreen" was an implementation detail, and it is what
 * left the two lenses looking at different pages.
 *
 * NO version-history panel lives here any more (PO, 2026-09-01). /assets has carried none since the CD
 * single-page layout — the 2026-08-16 ruling parked the restore UI ("snapshots still happen server-side;
 * the restore UI can find a home later") — and the roster's old BrandScreen wrapper turned out to be its
 * last mount, so deleting that wrapper orphaned it outright. Rather than keep an unreachable editor
 * waiting for a home nobody had chosen, it goes. Snapshots are STILL WRITTEN on every branding/photos/EPK
 * save and `/epk/versions` + `/epk/restore` still serve them, so the history is intact and a future home
 * is a UI to write, not data to recover.
 */

// ---- shared states -------------------------------------------------------------------------------------------

function LoadingRows() {
  return (
    <div className={styles.skeletons} role="status" aria-live="polite" aria-label="Loading">
      <Skeleton variant="tile" />
      <Skeleton variant="line" width="70%" />
      <Skeleton variant="line" width="45%" />
    </div>
  );
}

function ErrorState({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  return (
    <EmptyState
      title="Couldn't load"
      description={error instanceof ApiError ? error.message : "Please try again."}
      action={<Button onClick={onRetry}>Retry</Button>}
    />
  );
}

// ---- Logos ----------------------------------------------------------------------------------------------------

// CD slot map: White + Black are "Required", Color is "Optional"; the tile TONE names its background
// (the white mark previews on a dark tile).
const LOGO_SLOTS = [
  { key: "white", label: "White", tone: "dark" as const, tag: "Required" },
  { key: "black", label: "Black", tone: "light" as const, tag: "Required" },
  { key: "color", label: "Color", tone: "light" as const, tag: "Optional" },
];

export function LogosCard() {
  const q = useBranding();
  // Branch ONLY while no data exists — once loaded, the editor stays mounted through background
  // refetches (failed or successful) so unsaved edits never clobber (the COMPONENTS.md draft contract).
  if (!q.data) {
    return (
      // No subtitle (ART-187) — the PO cut the descriptive sub-text from every Core card; the slot tiles say it.
      <Card id="logos" title="Logos">
        {q.isError ? <ErrorState error={q.error} onRetry={() => void q.refetch()} /> : <LoadingRows />}
      </Card>
    );
  }
  return <LogosEditor initial={q.data} />;
}

function LogosEditor({ initial }: { initial: BrandingRead }) {
  const save = useUpdateBranding();
  const [logos, setLogos] = useState<Record<string, string | null>>({
    white: initial.white?.mediaId ?? null,
    black: initial.black?.mediaId ?? null,
    color: initial.color?.mediaId ?? null,
  });
  // Keep the server-resolved URL only while the slot still points at the same media (a fresh upload has no
  // resolved URL yet → useMediaObjectUrl falls back to the /raw fetch).
  const urls: Record<string, string | null | undefined> = {
    white: initial.white?.mediaId === logos.white ? initial.white?.url : undefined,
    black: initial.black?.mediaId === logos.black ? initial.black?.url : undefined,
    color: initial.color?.mediaId === logos.color ? initial.color?.url : undefined,
  };

  const { unsaved, markSaved } = useUnsavedChanges(logos);

  const onSave = () => {
    save.mutate(
      {
        logoWhiteMediaId: logos.white,
        logoBlackMediaId: logos.black,
        logoColorMediaId: logos.color,
        colors: initial.colors, // colours preserved round-trip (no editor on shot-29 — additive)
      },
      {
        // Reset the draft to the server-normalised result — the deliberate post-save sync now the
        // editor is not remounted by a dataUpdatedAt key (COMPONENTS.md draft contract). The unsaved
        // baseline moves to the SAME value, so a saved card goes quiet immediately.
        onSuccess: (saved) => {
          const next = {
            white: saved.white?.mediaId ?? null,
            black: saved.black?.mediaId ?? null,
            color: saved.color?.mediaId ?? null,
          };
          setLogos(next);
          markSaved(next);
          pushToast("Logos saved.", "success");
        },
        onError: (e) => pushToast(errorMessage(e, "Couldn't save logos."), "error"),
      },
    );
  };

  return (
    <Card
      id="logos"
      title="Logos"
      actions={
        <Button {...saveButtonState(unsaved, save.isPending)} size="xs" onClick={onSave}>
          {save.isPending ? "Saving…" : "Save logos"}
        </Button>
      }
    >
      <div className={styles.logoTiles}>
        {LOGO_SLOTS.map((slot) => (
          <LogoTile
            key={slot.key}
            label={slot.label}
            tone={slot.tone}
            tag={slot.tag}
            mediaId={logos[slot.key]}
            serverUrl={urls[slot.key]}
            onUploaded={(id) => setLogos((prev) => ({ ...prev, [slot.key]: id }))}
          />
        ))}
      </div>
    </Card>
  );
}

function LogoTile({ label, tone, tag, mediaId, serverUrl, onUploaded }: {
  label: string; tone: "dark" | "light"; tag: string; mediaId: string | null;
  serverUrl?: string | null; onUploaded: (mediaId: string) => void;
}) {
  const { uploadMedia } = useUploadMedia();
  const resolved = useMediaObjectUrl(mediaId, serverUrl, { variant: "sm" });
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // Below `md` the tile's short 3/2 preview clipped the name + format hint (ART-187), so the phone shows
  // just the upload icon; the foot names the slot there. Desktop keeps the fuller empty state.
  const isDesktop = useMinWidth("md");

  const onPick = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const asset = await uploadMedia(file, { kind: "image", purpose: "brand-logo" });
      if (asset.mediaId) onUploaded(asset.mediaId);
    } catch (err) {
      pushToast(errorMessage(err, "Couldn't upload the logo."), "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.logoTile}>
      {/* The PREVIEW is the control — one big target instead of a pill in the foot. Empty, it reads as
          the drop zone it is (icon over the slot name); filled, the tone backdrop returns so a white
          mark is visible on ink, and the same icon comes back on hover/focus to say "replace". */}
      <button
        type="button"
        className={`${styles.logoPreview} ${mediaId && resolved.url && tone === "dark" ? styles.toneDark : styles.toneLight}`}
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        aria-label={`${mediaId ? "Replace" : "Upload"} the ${label.toLowerCase()} logo`}
      >
        {busy ? <Skeleton variant="tile" />
          : mediaId && resolved.url ? (
            <>
              {/* The button already names the slot; a repeated alt would just double the announcement. */}
              <img src={resolved.url} alt="" className={styles.logoImg} />
              <span className={styles.logoHover} aria-hidden="true">
                <span className={styles.logoIcon}><Icon icon={Upload} size={20} /></span>
              </span>
            </>
          ) : isDesktop ? (
            <span className={styles.logoEmpty}>
              <span className={styles.logoIcon}><Icon icon={Upload} size={20} /></span>
              <span className={styles.logoEmptyName}>{label} logo</span>
              <span className={styles.logoEmptyHint}>PNG / SVG</span>
            </span>
          ) : (
            // Phone: just the upload icon — the foot below names the slot (ART-187).
            <span className={styles.logoIcon}><Icon icon={Upload} size={20} /></span>
          )}
      </button>
      <div className={styles.logoFoot}>
        <span className={styles.logoLabel}>{label}</span>
        <span className={styles.logoTag}>{tag}</span>
      </div>
      <input ref={inputRef} type="file" accept="image/*" className={styles.hiddenInput} onChange={onPick} />
    </div>
  );
}

// ---- Photos ---------------------------------------------------------------------------------------------------

type Draft = {
  photoId: string | null; slot: number; mediaId: string; url: string | null; thumbUrl: string | null;
  contentType: string | null; width: number | null; height: number | null;
  epkRole: string | null; caption: string | null; isAntennaAvatar: boolean; isAntennaCover: boolean;
};

const toDraft = (p: BandPhotoView): Draft => ({
  photoId: p.photoId ?? null, slot: p.slot ?? 0, mediaId: p.mediaId ?? "", url: p.url ?? null, thumbUrl: p.thumbUrl ?? null,
  contentType: p.contentType ?? null, width: p.width ?? null, height: p.height ?? null,
  epkRole: p.epkRole ?? null, caption: p.caption ?? null,
  isAntennaAvatar: p.isAntennaAvatar ?? false, isAntennaCover: p.isAntennaCover ?? false,
});

export function PhotosCard() {
  const q = useBandPhotos();
  // Branch ONLY while no data exists (the draft contract — see LogosCard).
  if (!q.data) {
    return (
      // No subtitle (ART-187) — the sub-text was cut from every Core card; the slot rows/tiles carry the meaning.
      <Card id="photos" title="Photos">
        {q.isError ? <ErrorState error={q.error} onRetry={() => void q.refetch()} /> : <LoadingRows />}
      </Card>
    );
  }
  return <PhotosEditor initial={q.data} />;
}

function PhotosEditor({ initial }: { initial: BandPhotosRead }) {
  const save = useUpdatePhotos();
  const isDesktop = useMinWidth("md");
  const [drafts, setDrafts] = useState<Draft[]>(() => (initial.photos ?? []).map(toDraft));
  // ONE unsaved state for BOTH presentations below — the phone tiles and the desktop rows are two views
  // of this same draft, so a change made on one must light the other's Save.
  const { unsaved, markSaved } = useUnsavedChanges(drafts);

  // One surface per photo (CD placement rule): assigning a placement evicts whatever slot currently
  // holds it — explicitly OR through its implicit default — back to the OFF state. The eviction
  // materialises the default (NO_PLACEMENT is stored) so the display can't show two holders.
  const setEpkRole = (slot: number, epkRole: EpkRole) =>
    setDrafts((prev) =>
      prev.map((d) =>
        d.slot === slot
          ? { ...d, epkRole }
          : epkRole !== NO_PLACEMENT && effectivePlacement(d) === epkRole
            ? { ...d, epkRole: NO_PLACEMENT }
            : d,
      ),
    );

  // The Antenna control is 3-state (off → avatar → cover → off); avatar/cover are one-per-band, so setting one
  // clears the same flag on every other slot (mirrors the server's clear-then-set).
  const cycleAntenna = (slot: number) =>
    setDrafts((prev) => {
      const cur = prev.find((d) => d.slot === slot);
      if (!cur) return prev;
      const next = cur.isAntennaAvatar ? "cover" : cur.isAntennaCover ? "off" : "avatar";
      return prev.map((d) =>
        d.slot === slot
          ? { ...d, isAntennaAvatar: next === "avatar", isAntennaCover: next === "cover" }
          : { ...d, isAntennaAvatar: next === "avatar" ? false : d.isAntennaAvatar, isAntennaCover: next === "cover" ? false : d.isAntennaCover },
      );
    });

  const onUploaded = (slot: number, mediaId: string, url: string | null, contentType: string | null) =>
    setDrafts((prev) => {
      const existing = prev.find((d) => d.slot === slot);
      if (existing)
        return prev.map((d) => (d.slot === slot ? { ...d, mediaId, url, thumbUrl: null, contentType } : d));
      return [...prev, {
        photoId: null, slot, mediaId, url, thumbUrl: null,
        contentType, width: null, height: null,
        epkRole: null, caption: null, isAntennaAvatar: false, isAntennaCover: false,
      }].sort((a, b) => a.slot - b.slot);
    });

  const onSwap = (fromSlot: number, toSlot: number) =>
    setDrafts((prev) => swapPhotoMedia(prev, fromSlot, toSlot));

  const onSave = () => {
    save.mutate(
      drafts.map((d) => ({
        slot: d.slot, mediaId: d.mediaId, epkRole: d.epkRole, caption: d.caption,
        isAntennaAvatar: d.isAntennaAvatar, isAntennaCover: d.isAntennaCover,
      })),
      {
        // Post-save draft sync from the response (photoIds/thumbs are server-assigned) — the
        // COMPONENTS.md draft contract now the editor is not remounted.
        onSuccess: (saved) => {
          const next = (saved.photos ?? []).map(toDraft);
          setDrafts(next);
          markSaved(next);
          pushToast("Photos saved.", "success");
        },
        onError: (e) => pushToast(errorMessage(e, "Couldn't save photos."), "error"),
      },
    );
  };

  // The phone gets a DIFFERENT component (CD mobile Assets: image tiles, not rows) driven by the
  // same drafts/save/eviction state — one source of truth, two presentations.
  if (!isDesktop) {
    // Whichever FILLED slot holds the Dashboard Page placement — explicitly or by its slot
    // default. An empty slot can't be the hero, so it never wears the tag.
    const heroDraft = drafts.find((d) => effectivePlacement(d) === "Dashboard Page");
    return (
      <Card
        id="photos"
        title="Photos"
        actions={
          <Button {...saveButtonState(unsaved, save.isPending)} size="xs" onClick={onSave}>
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        }
      >
        <MobilePhotoTiles
          slots={PHOTO_SLOTS}
          drafts={drafts}
          heroSlot={heroDraft?.slot ?? null}
          onUploaded={onUploaded}
          onMakeHero={(slot) => setEpkRole(slot, "Dashboard Page")}
          onRemove={(slot) => setDrafts((prev) => prev.filter((x) => x.slot !== slot))}
        />
      </Card>
    );
  }

  return (
    <Card
      id="photos"
      title="Photos"
      actions={
        <Button {...saveButtonState(unsaved, save.isPending)} size="xs" onClick={onSave}>
          {save.isPending ? "Saving…" : "Save"}
        </Button>
      }
    >
      {/* All FIVE named rows always render (CD layout) — an empty slot is a row with the real
          placeholder copy and its own Upload, never a hidden slot behind an "add" button. */}
      <ul className={styles.photoRows}>
        {PHOTO_SLOTS.map((def) => (
          <PhotoRow
            key={def.slot}
            def={def}
            draft={drafts.find((d) => d.slot === def.slot) ?? null}
            onEpkRole={(role) => setEpkRole(def.slot, role)}
            onCycleAntenna={() => cycleAntenna(def.slot)}
            onUploaded={(mediaId, url, contentType) => onUploaded(def.slot, mediaId, url, contentType)}
            onRemove={() => setDrafts((prev) => prev.filter((x) => x.slot !== def.slot))}
            onSwap={(fromSlot) => onSwap(fromSlot, def.slot)}
          />
        ))}
      </ul>
    </Card>
  );
}

function PhotoRow({ def, draft, onEpkRole, onCycleAntenna, onUploaded, onRemove, onSwap }: {
  def: (typeof PHOTO_SLOTS)[number]; draft: Draft | null; onEpkRole: (v: EpkRole) => void;
  onCycleAntenna: () => void; onUploaded: (mediaId: string, url: string | null, contentType: string | null) => void;
  onRemove: () => void; onSwap: (fromSlot: number) => void;
}) {
  const { uploadMedia } = useUploadMedia();
  const resolved = useMediaObjectUrl(draft?.mediaId ?? null, draft?.thumbUrl ?? draft?.url ?? null, { variant: "thumb" });
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const empty = draft === null;
  const antennaLabel = draft?.isAntennaAvatar ? "Antenna main" : draft?.isAntennaCover ? "Antenna background" : null;
  const dims = draft?.width && draft?.height ? `${draft.width}×${draft.height}` : null;
  const format = draft?.contentType?.split("/")[1]?.toUpperCase() ?? null;
  const meta = empty ? "Empty, upload or drop a shot" : [format, dims].filter(Boolean).join(" · ");
  const current = draft ? effectivePlacement(draft) : def.defaultRole;

  const uploadFile = async (file: File) => {
    setBusy(true);
    try {
      const asset = await uploadMedia(file, { kind: "image", purpose: "band-photo" });
      if (asset.mediaId) onUploaded(asset.mediaId, asset.url ?? null, asset.contentType ?? null);
    } catch (err) {
      pushToast(errorMessage(err, "Couldn't upload the photo."), "error");
    } finally {
      setBusy(false);
    }
  };

  const onPick = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) void uploadFile(file);
  };

  // The CD two-gesture contract: an OS FILE drop uploads into THIS slot; another row's THUMBNAIL
  // drop swaps the images only (placements + antenna stay with the slots).
  const acceptsDrag = (e: React.DragEvent) =>
    e.dataTransfer.types.includes(PHOTO_DRAG_TYPE) || e.dataTransfer.types.includes("Files");

  return (
    <li
      className={`${styles.photoRow} ${dragOver ? styles.photoRowOver : ""}`}
      onDragOver={(e) => {
        if (!acceptsDrag(e)) return;
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        setDragOver(false);
        const fromSlot = e.dataTransfer.getData(PHOTO_DRAG_TYPE);
        if (fromSlot) {
          e.preventDefault();
          onSwap(Number(fromSlot));
          return;
        }
        const file = e.dataTransfer.files?.[0];
        if (file) {
          e.preventDefault();
          void uploadFile(file);
        }
      }}
    >
      <div
        className={styles.thumb}
        draggable={!empty}
        title={empty ? undefined : "Drag onto another row to swap images"}
        onDragStart={(e) => {
          if (empty) return;
          e.dataTransfer.setData(PHOTO_DRAG_TYPE, String(def.slot));
          e.dataTransfer.effectAllowed = "move";
        }}
      >
        {busy ? <Skeleton variant="tile" /> : resolved.url ? <img src={resolved.url} alt={def.name} /> : <span className={styles.thumbEmpty} />}
      </div>
      <div className={styles.photoMain}>
        <p className={styles.photoName}>{def.name}</p>
        <p className={styles.meta}>
          {meta}
          {antennaLabel && <span className={styles.antennaTag}> · {antennaLabel}</span>}
        </p>
      </div>
      <div className={styles.photoSide}>
        <button
          type="button"
          className={`${styles.antennaToggle} ${
            draft?.isAntennaAvatar ? styles.antennaMain : draft?.isAntennaCover ? styles.antennaCover : ""
          }`}
          aria-pressed={antennaLabel !== null}
          aria-label={`Antenna role for ${def.name}: ${antennaLabel ?? "off"}`}
          title={antennaLabel ?? "Set Antenna avatar / background"}
          onClick={onCycleAntenna}
          disabled={empty} // CD: disabled on an empty slot
        >
          {/* The REAL Antenna tower mark (CD: antenna-68; states none→muted, main→accent, background→ink). */}
          <AntennaMark size={16} />
        </button>
        {/* Placement DROPDOWN (CD control): a crimson pill Picker in the VOCABULARY's own order, so "None"
            leads on every row and the four pages follow in a fixed sequence (PO, 2026-09-07). It used to
            hoist each slot's own default to the top — the CD per-row order — which meant the list read
            differently on all five rows and the off state moved around inside it.
            A placement lives ON the photo row, so an empty slot can't hold one — disabled until upload.
            (The old native-<select> version needed a width-ghost hack because a <select> sizes to its
            WIDEST option; a Picker chip is a button, and a button hugs its content.) */}
        <Picker
          label={`${def.name} placement`}
          value={current}
          disabled={empty}
          options={EPK_ROLES.map((r) => ({ value: r, label: r }))}
          renderTrigger={() => (
            <span
              className={empty ? `${styles.placement} ${styles.placementOff}` : styles.placement}
              title={empty ? "Upload a photo first" : undefined}
            >
              {current}
              <Icon icon={ChevronDown} size={16} />
            </span>
          )}
          onChange={(v) => onEpkRole(v as EpkRole)}
        />
        <Button variant="ghost" size="xs" onClick={() => inputRef.current?.click()} disabled={busy}>
          {empty ? "Upload" : "Replace"}
        </Button>
        {!empty && (
          <button type="button" className={styles.remove} onClick={onRemove} aria-label={`Remove ${def.name}`}>
            <Icon icon={X} size={16} />
          </button>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*" className={styles.hiddenInput} onChange={onPick} />
    </li>
  );
}

// ---- Bios -----------------------------------------------------------------------------------------------------

// TWO cards (CD layout: "Short bio" and "Full bio" are separate cards), and BOTH cards write BOTH
// fields (the CD rule: saving one persists shortBio AND fullBio, so one card can never wipe the
// other's SAVED value — the unedited field rides along at its last-known-server state).
export function BiosCard() {
  return (
    <div className={styles.biosGrid}>
      <ShortBioCard />
      <FullBioCard />
    </div>
  );
}

// No subtitle (ART-187) — the bio field labels + counters already say what each is; the helper caption was
// clutter the PO called unnecessary.
const BIO_SHELL = {
  short: { id: "short-bio", title: "Short bio" },
  full: { id: "full-bio", title: "Full bio" },
} as const;

export function ShortBioCard() {
  const q = useBios();
  // Branch ONLY while no data exists (the draft contract — see LogosCard).
  if (!q.data) {
    return (
      <Card {...BIO_SHELL.short}>
        {q.isError ? <ErrorState error={q.error} onRetry={() => void q.refetch()} /> : <LoadingRows />}
      </Card>
    );
  }
  return <BioEditor initial={q.data} field="short" />;
}

export function FullBioCard() {
  const q = useBios();
  // Branch ONLY while no data exists (the draft contract — see LogosCard).
  if (!q.data) {
    return (
      <Card {...BIO_SHELL.full}>
        {q.isError ? <ErrorState error={q.error} onRetry={() => void q.refetch()} /> : <LoadingRows />}
      </Card>
    );
  }
  return <BioEditor initial={q.data} field="full" />;
}

function BioEditor({ initial, field }: { initial: BioRead; field: "short" | "full" }) {
  const save = useUpdateBios();
  const [value, setValue] = useState((field === "short" ? initial.shortBio : initial.fullBio) ?? "");
  const { unsaved, markSaved } = useUnsavedChanges(value);
  const isShort = field === "short";
  const max = isShort ? SHORT_BIO_MAX : FULL_BIO_MAX;
  const over = value.length > max;

  const onSave = () => {
    if (over) return;
    // Both fields ride every save (CD rule) — this card's edit + the OTHER field's last server value.
    save.mutate(
      {
        shortBio: (isShort ? value.trim() : (initial.shortBio ?? "")) || null,
        fullBio: (isShort ? (initial.fullBio ?? "") : value.trim()) || null,
      },
      {
        // Post-save draft sync from the response (the COMPONENTS.md draft contract).
        onSuccess: (saved) => {
          // The server TRIMS, so the value coming back can differ from what was typed — the baseline has to
          // be the returned one or a trailing space would leave the card looking permanently unsaved.
          const next = (isShort ? saved.shortBio : saved.fullBio) ?? "";
          setValue(next);
          markSaved(next);
          pushToast(isShort ? "Short bio saved." : "Full bio saved.", "success");
        },
        onError: (e) => pushToast(errorMessage(e, "Couldn't save the bio."), "error"),
      },
    );
  };

  return (
    <Card
      {...BIO_SHELL[field]}
      actions={
        <Button {...saveButtonState(unsaved, save.isPending || over)} size="xs" onClick={onSave}>
          {save.isPending ? "Saving…" : "Save"}
        </Button>
      }
    >
      <Textarea
        label={isShort ? "Short bio" : "Full bio"}
        // The card title already names the field, so the visible label was the same words twice (ART-187:
        // "Short and long are on there twice. Kill the small one"). Kept for screen readers via hideLabel.
        hideLabel
        rows={isShort ? 3 : 8}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        // Both bios carry the counter. The full bio had none, so the only thing between a band and a
        // rejected save was the server's 422 (ART-55) — a limit you can only discover by hitting it.
        hint={
          <span className={over ? styles.counterOver : undefined}>
            {value.length} / {max}
          </span>
        }
      />
    </Card>
  );
}
